import React, { useEffect, useState, useRef } from 'react'
import { Grid } from '@mui/material'
import axios from 'axios'
import ReactGA from 'react-ga4'
import { useNavigate } from 'react-router-dom'
import { AppConstants, LeadStage } from 'AppConstants/AppConstants'
import Footer from 'components/Footer'
import Header from 'components/Header'
import TextField from 'components/shared/textField'
import useQueryParams from 'hooks/useQueryParams'
import {
  SetFirstNameSignUp,
  SetFormattedPhoneNumberSignUp,
  SetPhoneNumberSignUp,
  SetTrackerCountSignUp,
} from 'redux/SignUp/persist'
import { useAppDispatch, useAppSelector } from 'redux/store'
import { createNavigateWithQueryParams } from 'utils/navigateWithQueryParams'
import { validateEmail } from 'utils/validateEmail'
import styles from './Name.module.scss'

const NameSignUp: React.FC = () => {
  const { getAllParams, getParams, hasCampaign } = useQueryParams()
  const utm_source = getParams('utm_source')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [firstname, setFirstname] = useState('')
  const [email, setEmail] = useState('')
  const [disableBtn, setDisableBtn] = useState(true)
  const emailRef = useRef<HTMLInputElement>(null)

  // Signup flow state management
  const isMoreThanOneStudio = useAppSelector((state) => state.signUpPersist.isMoreThanOneStudioSignUp)
  const trackerCount = useAppSelector((state) => state.signUpPersist.trackerCountSignUp)

  // Create reusable navigation function
  const navigateWithQueryParams = createNavigateWithQueryParams(navigate, getAllParams)

  const calculateTrackerCount = () => {
    // If has no query params - Direct flow: 8 pages
    if (!hasCampaign) {
      return 8
    }

    // If has query params, check for 'loc' parameter
    const locParam = getParams('loc')

    // If loc param is there, show 6 pages
    if (locParam) {
      return 6
    }

    // If loc param is not there, show 7 pages
    return 7
  }

  const onContinue = async () => {
    if (firstname.length !== 0 && email.length !== 0 && validateEmail(email)) {
      // Store in localStorage for backward compatibility
      localStorage.setItem(AppConstants.USER_FIRST_NAME, firstname)
      localStorage.setItem(AppConstants.USER_EMAIL, email)

      // Store in Redux signup flow state
      dispatch(SetFirstNameSignUp(firstname))

      // Navigate to next step in signup flow with query params
      navigateWithQueryParams('/signup/phone')
    }
  }

  useEffect(() => {
    // Calculate and store tracker count in Redux
    const trackerCount = calculateTrackerCount()
    console.log('Calculated tracker count:', trackerCount)
    dispatch(SetTrackerCountSignUp(trackerCount))
    if (utm_source === 'google') {
      ReactGA.event('page_view', {
        page_title: 'SignUp|Name',
        page_location: `/signup/name`,
      })
    }
  }, [utm_source])

  // useEffect(() => {
  //   if (utm_source === 'facebook') {
  //     //@ts-ignore
  //     // fbq('track', 'ViewContent')
  //   }
  // }, [utm_source])

  useEffect(() => {
    localStorage.setItem(AppConstants.USER_FIRST_NAME, '')
    localStorage.setItem(AppConstants.USER_EMAIL, '')
    dispatch(SetPhoneNumberSignUp(''))
    dispatch(SetFormattedPhoneNumberSignUp(''))
  }, [])

  useEffect(() => {
    // Enable button only if both firstname and email are filled and email is valid
    if (firstname.length > 0 && email.length > 0 && validateEmail(email)) {
      setDisableBtn(false)
    } else {
      setDisableBtn(true)
    }
  }, [firstname, email])

  return (
    <div className={styles.container}>
      <Header
        headerText={''}
        showBackButton={true}
        onBack={() => {
          navigateWithQueryParams('/signup')
        }}
      ></Header>
      <div className={styles.content}>
        <div className={styles.textContainer}>
          <span>Let's get you scheduled</span>
          <br></br>
          <span>with our doctors. What's</span>
          <br></br>
          <span>your first name and email?</span>
        </div>
        <div className={styles.formContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                style={{ marginTop: '5px' }}
                sx={{
                  '& .MuiInputBase-input': {
                    textTransform: 'capitalize',
                  },
                  '@media (min-width:600px)': {
                    '& .MuiInputLabel-root.Mui-focused': {
                      height: '16px',
                    },
                  },
                }}
                placeholder="Enter first name"
                label="First Name"
                name="firstname"
                value={firstname}
                onChange={(e) => {
                  setFirstname(e.target.value)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    emailRef.current?.focus()
                  }
                }}
                className={styles.firstNameInput}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                inputRef={emailRef}
                style={{ marginTop: '5px' }}
                sx={{
                  '@media (min-width:600px)': {
                    '& .MuiInputLabel-root.Mui-focused': {
                      height: '16px',
                    },
                  },
                }}
                placeholder="Enter Email Address"
                label="Email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    disableBtn ? undefined : onContinue()
                  }
                }}
                className={styles.emailInput}
              />
            </Grid>
          </Grid>
        </div>
      </div>
      <Footer
        buttonText={'Continue'}
        isLoading={false}
        shouldShowFour={true}
        shouldShowFive={true}
        shouldShowSix={true}
        shouldShowSeven={trackerCount >= 7}
        shouldShowEight={trackerCount >= 8}
        trackerIndex={1}
        isDisabled={disableBtn}
        buttonId={'continue-btn'}
        buttonType={'button'}
        onButtonClick={() => {
          disableBtn ? undefined : onContinue()
        }}
      ></Footer>
    </div>
  )
}

export default NameSignUp
